import { getBackendURL } from "../components/env";
import { supabaseSchema } from "../components/supabaseClient";
import { staffRequest } from "./staffRequest";

// Who is asking (backend/api/compliance.py, Plan 4 Task 11): every call sends
// the signed-in user's token, with one refresh-and-retry after a 401
// (staffRequest), and the server takes the caller from it: their role, their
// clinics, and who exported or imported. No request names the signed-in user:
// a query carries only the filters each read names, and a body never the
// fields that could (SELF_IDS), whoever put one in.

const SELF_IDS = ["requested_by", "imported_by"];

function activeSchema(override) {
  if (override) return override;
  const raw = (supabaseSchema || "public").trim();
  return raw.includes(",") ? raw.split(",")[0].trim() : raw;
}

function complianceUrl(path, params = {}) {
  const url = new URL(`${getBackendURL()}/api/compliance${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== "") url.searchParams.set(key, String(value));
  });
  return url.toString();
}

function complianceGet(path, params, failure = "Compliance API error") {
  return staffRequest(complianceUrl(path, params), { failure });
}

// A POST with `body` (and the schema the screen is in) or, for an action on
// one alert or fill, none: the server takes its schema from the record.
function compliancePost(path, { body, schema, failure = "Compliance API error" } = {}) {
  if (body === undefined) return staffRequest(complianceUrl(path), { method: "POST", failure });
  const sent = { ...body, schema: activeSchema(schema) };
  SELF_IDS.forEach((key) => delete sent[key]);
  return staffRequest(complianceUrl(path), { method: "POST", body: sent, failure });
}

export function fetchComplianceAlerts(params = {}) {
  return complianceGet("/alerts/", {
    schema: activeSchema(params.schema),
    status: params.status,
    type: params.type,
    patient_id: params.patient_id,
    provider: params.provider,
  }, "Failed to load alerts");
}

export function fetchComplianceDashboard(schema, { provider, catalogVersion } = {}) {
  return complianceGet("/dashboard/", {
    schema: activeSchema(schema),
    provider,
    catalog_version: catalogVersion,
  }, "Failed to load dashboard");
}

export function fetchComplianceProviders(schema) {
  return complianceGet("/providers/", { schema: activeSchema(schema) });
}

export function fetchComplianceTasks(params = {}) {
  return complianceGet("/tasks/", { schema: activeSchema(params.schema), status: params.status }, "Failed to load tasks");
}

export function fetchComplianceCatalog(schema, catalogVersion) {
  return complianceGet("/catalog/", {
    schema: activeSchema(schema),
    catalog_version: catalogVersion,
  }, "Failed to load catalog");
}

function exportFileName(res, schema) {
  const disposition = res.headers?.get?.("Content-Disposition") || "";
  const named = /filename="?([^";]+)"?/i.exec(disposition);
  return named ? named[1] : `compliance_export_${schema}.csv`;
}

// How long a downloaded export's object URL is kept: revoked at once, some
// browsers cancel the download it started.
const EXPORT_URL_KEPT_MS = 30000;

// The payer CSV, downloaded with the signed-in user's token (a plain link
// can't send one) and saved as a file. Resolves to the file's name.
export async function downloadComplianceExport({ provider, status = "all", schema, deidentify } = {}) {
  const s = activeSchema(schema);
  const res = await staffRequest(
    complianceUrl("/export/", { schema: s, provider, status, deidentify: deidentify ? "1" : undefined }),
    { raw: true, failure: "Export failed" },
  );
  const name = exportFileName(res, s);
  const href = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), EXPORT_URL_KEPT_MS);
  return name;
}

export function fetchCompliancePatient(patientId, schema) {
  return complianceGet(`/patient/${encodeURIComponent(patientId)}/`, { schema: activeSchema(schema) });
}

export function runComplianceEvaluate(schema) {
  return compliancePost("/evaluate/", { body: {}, schema });
}

export function acknowledgeComplianceAlert(alertId) {
  return compliancePost(`/alerts/${alertId}/acknowledge/`);
}

export function resolveComplianceAlert(alertId) {
  return compliancePost(`/alerts/${alertId}/resolve/`);
}

export function documentComplianceChart(alertId) {
  return compliancePost(`/alerts/${alertId}/document-chart/`);
}

export function createComplianceOutreachTask(alertId) {
  return compliancePost(`/alerts/${alertId}/create-task/`);
}

export function orderComplianceTest(alertId, approved = true) {
  return compliancePost(`/alerts/${alertId}/order-test/`, { body: { approved } });
}

export function recordMedicationFill(body) {
  return compliancePost("/fill-events/", { body });
}

export function notifyCompliancePatient(alertId) {
  return compliancePost(`/alerts/${alertId}/notify-patient/`);
}

export async function importComplianceCareGaps(file, schema) {
  const form = new FormData();
  form.append("file", file);
  form.append("schema", activeSchema(schema));
  return staffRequest(complianceUrl("/care-gaps/import/"), {
    method: "POST",
    form,
    failure: "Care gap import failed",
  });
}

export async function importComplianceCareGapsJson(gaps, schema) {
  return compliancePost("/care-gaps/import/", { body: { gaps }, schema });
}

export function fetchMedicationFills(params = {}) {
  return complianceGet("/medication-fills/", {
    schema: activeSchema(params.schema),
    patient_id: params.patient_id,
  }, "Failed to load fills");
}

export function deleteMedicationFill(fillId) {
  return compliancePost(`/medication-fills/${fillId}/delete/`);
}
